import { c as _c } from "react/compiler-runtime";
import { RechartsAreaChart as AreaChart, RechartsBarChart as BarChart, RechartsComposedChart as ComposedChart, RechartsLineChart as LineChart, RechartsPieChart as PieChart, RechartsScatterChart as ScatterChart } from '@precis-digital/kurama/recharts';
import { styled } from '@precis-digital/kurama/utils';
import { type CSSObject, type Theme } from '@precis-digital/kurama/theme';
import { type ComponentType } from 'react';
import type { CartesianChartType, ComposableChartTypes, PolarChartType, RechartsChartProps, ScatterChartType } from '../types';
import { assertNever } from '../utils/generics';
import { useChartCardDefaults } from '../hooks/useChartCardDefaults';
export type ChartElementProps = ({
  chartType: ScatterChartType;
  otherChartType: undefined;
} & RechartsChartProps<ScatterChartType>) | ({
  chartType: CartesianChartType;
  otherChartType: undefined;
} & RechartsChartProps<CartesianChartType>) | ({
  chartType: ComposableChartTypes;
  otherChartType: ComposableChartTypes;
} & RechartsChartProps<'composed'>) | ({
  chartType: PolarChartType;
  otherChartType: undefined;
} & RechartsChartProps<PolarChartType>);
const ChartElement: ComponentType<ChartElementProps> = t0 => {
  const $ = _c(32);
  let chartType;
  let otherChartType;
  let props;
  if ($[0] !== t0) {
    ({
      chartType,
      otherChartType,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = chartType;
    $[2] = otherChartType;
    $[3] = props;
  } else {
    chartType = $[1];
    otherChartType = $[2];
    props = $[3];
  }
  const defaults = useChartCardDefaults();
  if (otherChartType != null) {
    const t1 = defaults.chart[chartType];
    const t2 = defaults.chart[otherChartType];
    let t3;
    if ($[4] !== defaults.chart.composed || $[5] !== props || $[6] !== t1 || $[7] !== t2) {
      t3 = {
        ...t1,
        ...t2,
        ...defaults.chart.composed,
        ...props
      };
      $[4] = defaults.chart.composed;
      $[5] = props;
      $[6] = t1;
      $[7] = t2;
      $[8] = t3;
    } else {
      t3 = $[8];
    }
    const mergedProps = t3;
    const t4 = mergedProps as RechartsChartProps<'composed'>;
    let t5;
    if ($[9] !== t4) {
      t5 = <StyledComposedChart {...t4} />;
      $[9] = t4;
      $[10] = t5;
    } else {
      t5 = $[10];
    }
    return t5;
  }
  switch (chartType) {
    case "scatter":
      {
        const t1 = props as RechartsChartProps<'scatter'>;
        let t2;
        if ($[11] !== defaults.chart.scatter || $[12] !== t1) {
          t2 = <StyledScatterChart {...defaults.chart.scatter} {...t1} />;
          $[11] = defaults.chart.scatter;
          $[12] = t1;
          $[13] = t2;
        } else {
          t2 = $[13];
        }
        return t2;
      }
    case "bar":
      {
        const t1 = props as RechartsChartProps<'bar'>;
        let t2;
        if ($[14] !== defaults.chart.bar || $[15] !== t1) {
          t2 = <StyledBarChart {...defaults.chart.bar} {...t1} />;
          $[14] = defaults.chart.bar;
          $[15] = t1;
          $[16] = t2;
        } else {
          t2 = $[16];
        }
        return t2;
      }
    case "line":
      {
        const t1 = props as RechartsChartProps<'line'>;
        let t2;
        if ($[17] !== defaults.chart.line || $[18] !== t1) {
          t2 = <StyledLineChart {...defaults.chart.line} {...t1} />;
          $[17] = defaults.chart.line;
          $[18] = t1;
          $[19] = t2;
        } else {
          t2 = $[19];
        }
        return t2;
      }
    case "gradientLine":
      {
        const t1 = props as RechartsChartProps<'area'>;
        let t2;
        if ($[20] !== defaults.chart.area || $[21] !== t1) {
          t2 = <StyledAreaChart {...defaults.chart.area} {...t1} />;
          $[20] = defaults.chart.area;
          $[21] = t1;
          $[22] = t2;
        } else {
          t2 = $[22];
        }
        return t2;
      }
    case "area":
      {
        const t1 = props as RechartsChartProps<'area'>;
        let t2;
        if ($[23] !== defaults.chart.area || $[24] !== t1) {
          t2 = <StyledAreaChart {...defaults.chart.area} {...t1} />;
          $[23] = defaults.chart.area;
          $[24] = t1;
          $[25] = t2;
        } else {
          t2 = $[25];
        }
        return t2;
      }
    case "pie":
      {
        const t1 = props as RechartsChartProps<'pie'>;
        let t2;
        if ($[26] !== defaults.chart.pie || $[27] !== t1) {
          t2 = <StyledPieChart {...defaults.chart.pie} {...t1} />;
          $[26] = defaults.chart.pie;
          $[27] = t1;
          $[28] = t2;
        } else {
          t2 = $[28];
        }
        return t2;
      }
    case "donut":
      {
        const t1 = props as RechartsChartProps<'donut'>;
        let t2;
        if ($[29] !== defaults.chart.donut || $[30] !== t1) {
          t2 = <StyledPieChart {...defaults.chart.donut} {...t1} />;
          $[29] = defaults.chart.donut;
          $[30] = t1;
          $[31] = t2;
        } else {
          t2 = $[31];
        }
        return t2;
      }
    default:
      {
        assertNever(chartType);
      }
  }
};

// Style applied to all Recharts charts.
const chartStyle = ({
  theme
}: {
  theme: Theme;
}): CSSObject => ({
  // Prevent selection of axis labels, legend text, and other chart text (e.g. when zooming/dragging).
  userSelect: 'none',
  // Reset inherited Card text colour so Recharts axis/legend labels use theme tokens in both modes.
  color: theme.tokens.foreground.main.primary,
  // Recharts allows clicking on the chart, making some chart components 'focused'.
  // This rule is used to hide the border that gets displayed when the chart is focused.
  '& *:focus:not(:focus-visible)': {
    outline: 'none'
  },
  // are used to clip the graphical elements to the actual plot area.
  // This is needed when zooming in on the chart, otherwise the graphical elements will be displayed outside the plot area.
  // This is needed when zooming in on the chart, otherwise the graphical elements will be displayed outside the plot area.
  '& .recharts-surface': {
    overflow: 'hidden'
  },
  '& .recharts-layer.recharts-line-dots, & .recharts-layer.recharts-area-dots, & .recharts-layer.recharts-bar-rectangles': {
    clipPath: 'inset(0)' // Add this - clips to parent bounds
  },
  // Hide the active dots while zooming
  '[data-zooming="true"] .recharts-active-dot': {
    display: 'none'
  }
});
const StyledComposedChart = styled(ComposedChart)(chartStyle);
const StyledBarChart = styled(BarChart)(chartStyle);
const StyledLineChart = styled(LineChart)(chartStyle);
const StyledAreaChart = styled(AreaChart)(chartStyle);
const StyledPieChart = styled(PieChart)(chartStyle);
const StyledScatterChart = styled(ScatterChart)(chartStyle);
export default ChartElement;